import React, { useRef } from 'react'
import { AnimatePresence, motion } from 'motion/react'

interface ConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  confirmButtonClass?: string
  isLoading?: boolean
  /** Extra content between the message and the buttons, e.g. a typed
   *  confirmation input. */
  children?: React.ReactNode
  /** Keeps Confirm disabled, e.g. until that input matches. */
  confirmDisabled?: boolean
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmButtonClass = 'bg-danger hover:bg-danger-dark text-white',
  isLoading = false,
  children,
  confirmDisabled = false
}) => {
  // A backdrop click closes only when the press *started* on the backdrop too.
  //
  // Two things this rules out, both of which dismiss a modal the user did not
  // mean to dismiss. The loud one is touch: a tap or a hold that opens a modal
  // is followed by a compatibility mouse click at the same coordinates, which
  // hit-tests against whatever is there *now* — the new modal's backdrop — so
  // the modal opened and shut again before it was visible. (Reproduced at
  // 390px opening this one from the shortcut settings modal's Delete.) The
  // quiet one is a drag that begins inside the card and releases outside it,
  // which `e.target === e.currentTarget` alone counts as a backdrop click.
  //
  // A compatibility click has no pointer events of its own, so requiring a
  // matching `pointerdown` excludes it without a timer or a coordinate check.
  const pressStartedOnBackdrop = useRef(false)

  const handleBackdropPointerDown = (e: React.PointerEvent) => {
    pressStartedOnBackdrop.current = e.target === e.currentTarget
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    const started = pressStartedOnBackdrop.current
    pressStartedOnBackdrop.current = false
    // `detail === 0` is a keyboard-activated click, which never has a pointer
    // press behind it and never means "dismiss".
    if (started && e.detail !== 0 && e.target === e.currentTarget && !isLoading) {
      onClose()
    }
  }

  const handleConfirm = () => {
    if (!isLoading && !confirmDisabled) {
      onConfirm()
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-white/75 z-50 flex items-center justify-center p-4"
          onPointerDown={handleBackdropPointerDown}
          onClick={handleBackdropClick}
          style={{ backdropFilter: 'blur(1px)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <motion.div
            className="bg-white rounded-lg p-6 w-full max-w-md shadow-2xl border border-gray-200"
            initial={{ opacity: 0, scale: 0.9, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 4 }}
            transition={{ type: 'spring', stiffness: 380, damping: 28 }}
          >
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            </div>

            <div className="mb-6">
              <p className="text-gray-700">{message}</p>
              {children}
            </div>

            <div className="flex justify-end space-x-3">
              <motion.button
                onClick={onClose}
                disabled={isLoading}
                whileTap={{ scale: 0.96 }}
                className="px-4 py-2 text-gray-700 bg-secondary hover:bg-secondary-dark rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cancelText}
              </motion.button>
              <motion.button
                onClick={handleConfirm}
                disabled={isLoading || confirmDisabled}
                whileTap={{ scale: 0.96 }}
                className={`px-4 py-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center ${confirmButtonClass}`}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  confirmText
                )}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ConfirmationModal
